import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError, EMPTY } from 'rxjs';
import { Auth } from '../services/auth/auth';
import { ModalService, ToastService, toastEnum } from '@ui-lib';
import { TranslocoService } from '@jsverse/transloco';
import { HttpStatusCode } from '@common/enums/http-status-codes.enum';
import { ROUTE_PATHS } from '@common/utils/route-paths.const';
import { SKIP_NOT_FOUND_REDIRECT } from '@common/utils/http-context-tokens';
import { openErrorModal } from './utils/interceptor-modal';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(Auth);
  const toastService = inject(ToastService);
  const modalService = inject(ModalService);
  const translocoService = inject(TranslocoService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      switch (error.status) {
        case HttpStatusCode.UNAUTHORIZED:
          authService.clearAuthData();
          router.navigate([ROUTE_PATHS.AUTH, ROUTE_PATHS.LOGIN], {
            state: { byPassDeactivateGuard: true },
          });
          toastService.showToast({
            title: error.error.message,
            status: toastEnum.Error,
          });
          return EMPTY;

        case HttpStatusCode.NOT_FOUND:
          // Requests that opt out (e.g. an empty sub-resource collection the
          // backend returns as 404) handle their own 404 locally — don't redirect.
          if (req.context.get(SKIP_NOT_FOUND_REDIRECT)) {
            return throwError(() => error);
          }
          // Handle not found errors (404) - navigate to not found page (page itself provides feedback)
          router.navigate([ROUTE_PATHS.STATUS, ROUTE_PATHS.NOT_FOUND], {
            state: { byPassDeactivateGuard: true },
            replaceUrl: true,
          });
          return EMPTY;

        case HttpStatusCode.SERVICE_UNAVAILABLE:
          // Handle service unavailable errors (503) - Maintenance Mode
          router.navigate([ROUTE_PATHS.STATUS, ROUTE_PATHS.MAINTENANCE], {
            state: { byPassDeactivateGuard: true },
            replaceUrl: true,
          });
          return EMPTY;

        case HttpStatusCode.NETWORK_FAILURE:
          // Handle network connection loss (status 0 indicates network failure)
          window.location.reload();
          return EMPTY;

        case HttpStatusCode.TOO_MANY_REQUESTS:
        case HttpStatusCode.INTERNAL_SERVER_ERROR:
          return openErrorModal(translocoService, modalService, {
            testId: 'unexpected_error_dialog',
            titleTestId: 'unexpected_error_dialog_title',
            messageTestId: 'unexpected_error_dialog_description',
            type: 'error',
            titleKey: 'common_unexpected_error',
            messageKey: 'common_error_description',
            buttons: [
              {
                id: 'cancel',
                testId: 'unexpected_error_dialog_cancel_button',
                label: 'general_app_cancel_button_label',
                variant: 'tertiary',
                btnClass: 'w-full',
                onClick: () => modalService.close(),
              },
            ],
          });
      }

      return throwError(() => error);
    })
  );
};
